import * as React from "react";
import type { Lang } from "./Landing.js";

/**
 * /privacidade and /termos (THE-7062, US-INT-1 AC2). Public, static, no network call.
 *
 * Every legal claim here restates behaviour the code enforces (nickname-only participants, 30-day
 * free retention, billing dark). The wording itself is NOT approved legal text: each page carries
 * the `[CONFIRMAR]` marker until Advogalia/Ivan sign off, and a test pins that marker.
 */
export type LegalKind = "privacy" | "terms";

interface LegalSection {
  heading: string;
  body: string;
}
interface LegalDoc {
  title: string;
  pending: string;
  sections: LegalSection[];
}

export const LEGAL_COPY: Record<Lang, Record<LegalKind, LegalDoc>> = {
  "pt-BR": {
    privacy: {
      title: "Política de privacidade",
      pending: "[CONFIRMAR] Texto provisório, pendente de revisão jurídica (Advogalia/Ivan).",
      sections: [
        { heading: "Quem somos", body: "O Klapzo é operado pela The Labs Group (SkilLab). Contato: contato@klapzo.app." },
        { heading: "Participantes", body: "Quem responde pelo celular entra anônimo, só com um apelido. Não pedimos nome, e-mail nem login, e não guardamos dado pessoal do participante." },
        { heading: "Facilitadores", body: "Para quem cria sessões, guardamos o e-mail usado no login e o conteúdo das sessões e agendas. O login é por link enviado ao e-mail." },
        { heading: "Retenção", body: "Os dados de uma sessão são apagados automaticamente depois do prazo do plano (30 dias no plano gratuito)." },
        { heading: "Seus direitos (LGPD)", body: "Você pode pedir acesso, correção ou exclusão dos seus dados escrevendo para contato@klapzo.app." },
      ],
    },
    terms: {
      title: "Termos de uso",
      pending: "[CONFIRMAR] Texto provisório, pendente de revisão jurídica (Advogalia/Ivan).",
      sections: [
        { heading: "O serviço", body: "O Klapzo oferece atividades ao vivo (enquete, quiz, nuvem de palavras, perguntas, sorteio e avaliação) e uma biblioteca de métodos de facilitação." },
        { heading: "Uso aceitável", body: "Não use o serviço para conteúdo ilegal, para assediar participantes ou para tentar acessar dados de outras contas." },
        { heading: "Planos", body: "Os limites de cada plano estão em /planos e na página inicial. A cobrança ainda não está ativa." },
        { heading: "Conteúdo", body: "O conteúdo que você cria continua sendo seu. Os métodos da biblioteca seguem a licença indicada em cada página." },
        { heading: "Contato", body: "Dúvidas sobre estes termos: contato@klapzo.app." },
      ],
    },
  },
  "en-US": {
    privacy: {
      title: "Privacy policy",
      pending: "[CONFIRMAR] Draft text, pending legal review (Advogalia/Ivan).",
      sections: [
        { heading: "Who we are", body: "Klapzo is operated by The Labs Group (SkilLab). Contact: contato@klapzo.app." },
        { heading: "Participants", body: "People answering on their phones join anonymously with a nickname only. We ask for no name, e-mail or login, and we store no personal data about participants." },
        { heading: "Facilitators", body: "For people who create sessions, we store the e-mail used to sign in and the content of sessions and agendas. Sign-in is by a link sent to that e-mail." },
        { heading: "Retention", body: "A session's data is deleted automatically after the plan's retention period (30 days on the free plan)." },
        { heading: "Your rights (LGPD)", body: "You can ask for access, correction or deletion of your data by writing to contato@klapzo.app." },
      ],
    },
    terms: {
      title: "Terms of use",
      pending: "[CONFIRMAR] Draft text, pending legal review (Advogalia/Ivan).",
      sections: [
        { heading: "The service", body: "Klapzo offers live activities (poll, quiz, word cloud, questions, picker and rating) and a library of facilitation methods." },
        { heading: "Acceptable use", body: "Do not use the service for illegal content, to harass participants or to try to reach other accounts' data." },
        { heading: "Plans", body: "Each plan's limits are on /planos and on the home page. Billing is not active yet." },
        { heading: "Content", body: "The content you create stays yours. Library methods follow the licence shown on each page." },
        { heading: "Contact", body: "Questions about these terms: contato@klapzo.app." },
      ],
    },
  },
  "es-LATAM": {
    privacy: {
      title: "Política de privacidad",
      pending: "[CONFIRMAR] Texto provisional, pendiente de revisión jurídica (Advogalia/Ivan).",
      sections: [
        { heading: "Quiénes somos", body: "Klapzo es operado por The Labs Group (SkilLab). Contacto: contato@klapzo.app." },
        { heading: "Participantes", body: "Quien responde desde el celular entra de forma anónima, solo con un apodo. No pedimos nombre, correo ni inicio de sesión, y no guardamos datos personales del participante." },
        { heading: "Facilitadores", body: "Para quien crea sesiones, guardamos el correo usado para entrar y el contenido de las sesiones y agendas. El acceso es por un enlace enviado a ese correo." },
        { heading: "Retención", body: "Los datos de una sesión se borran automáticamente después del plazo del plan (30 días en el plan gratuito)." },
        { heading: "Tus derechos (LGPD)", body: "Puedes pedir acceso, corrección o eliminación de tus datos escribiendo a contato@klapzo.app." },
      ],
    },
    terms: {
      title: "Términos de uso",
      pending: "[CONFIRMAR] Texto provisional, pendiente de revisión jurídica (Advogalia/Ivan).",
      sections: [
        { heading: "El servicio", body: "Klapzo ofrece actividades en vivo (encuesta, quiz, nube de palabras, preguntas, sorteo y valoración) y una biblioteca de métodos de facilitación." },
        { heading: "Uso aceptable", body: "No uses el servicio para contenido ilegal, para acosar a participantes ni para intentar acceder a datos de otras cuentas." },
        { heading: "Planes", body: "Los límites de cada plan están en /planos y en la página de inicio. El cobro aún no está activo." },
        { heading: "Contenido", body: "El contenido que creas sigue siendo tuyo. Los métodos de la biblioteca siguen la licencia indicada en cada página." },
        { heading: "Contacto", body: "Dudas sobre estos términos: contato@klapzo.app." },
      ],
    },
  },
};

export function LegalPage({ kind, lang }: { kind: LegalKind; lang: Lang }): React.ReactElement {
  const doc = LEGAL_COPY[lang][kind];
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-display text-display-md text-foreground mb-2">{doc.title}</h1>
      <p className="font-body text-sm text-muted mb-8" role="note">
        {doc.pending}
      </p>
      {doc.sections.map((s) => (
        <section key={s.heading} className="mb-6">
          <h2 className="font-display text-display-sm text-foreground mb-1">{s.heading}</h2>
          <p className="font-body text-base text-foreground">{s.body}</p>
        </section>
      ))}
    </main>
  );
}
